import React from 'react'

const tileserverRoot = (window.SETTINGS && window.SETTINGS.tileserverRoot) || 'http://127.0.0.1:8080/services/'

export const hotkeys = {
    navigation: {
        overview: 'o',
        explore: 'e',
        screen: 's',
        analyze: 'a'
    }
}

export const screeningCategories = [
    {
        label: 'Parcels must be located in the following',
        fields: [
            {
                label: 'Jurisdiction',
                fields: [
                    {
                        label: 'Within a City',
                        field: '2_3',
                        description: (
                            <p>
                                Includes parcels within city boundaries. City boundaries are current as of May 2023 and
                                generated by the California Department of Forestry and Fire Protection (CAL FIRE), which
                                processes and digitally captures annexations sent by the state Board of Equalization
                                (BOE).
                            </p>
                        )
                    },
                    {
                        label: 'Unincorporated Areas',
                        field: '2_4',
                        description: (
                            <p>
                                Includes parcels located within the boundaries of the unincorporated county. and
                                incorporated cities within the state of California. The boundaries are based on the
                                State Board of Equalization Tax Rate Area Maps for the 2015/16 assessment roll.
                            </p>
                        )
                    },
                    {
                        label: 'MPO',
                        field: '2_5',
                        description: (
                            <p>
                                Includes parcels within Metropolitan Planning Organization boundaries. These boundaries
                                are current as of Jan 1, 2010, accessed via the Census Bureau.
                            </p>
                        )
                    }
                ]
            },
            {
                label: 'Urbanized/Urban Areas',
                fields: [
                    {
                        label: 'Urbanized Area or Urban Cluster under Census',
                        field: '2_7',
                        description: (
                            <p>
                                Includes parcels within an Urbanized Area or Urban Cluster from the urban area
                                boundaries dataset from the 2020 Census Bureau.
                            </p>
                        )
                    },
                    {
                        label: 'Urbanized Area as defined in PRC 21071',
                        field: '2_1',
                        description: (
                            <p>
                                Includes parcels within an Urbanized Area as defined in Public Resources Code 21071,
                                which represents incorporated cities (2023) that meet the criteria in subdivision (a)
                                and unincorporated islands (areas completely surrounded by one or more incorporated
                                cities) that meet the criteria in subdivision (b)(1)(A). Note, parcels meeting the
                                criteria in subdivision (b)(1)(B) are not captured at this time. Parcels in the
                                unincorporated area must meet additional criteria contained in subdivision (b)(2).
                            </p>
                        )
                    },
                    {
                        label: 'Urban Area as defined in PRC 21094.5',
                        field: '2_2',
                        description: (
                            <p>
                                Includes parcels within an Urban Area as defined in PRC 21094.5, which represents
                                incorporated cities (2023) and unincorporated islands (areas completely surrounded by
                                one or more incorporated cities) that meet the following criteria: (1) the population of
                                the unincorporated area and the population of the surrounding incorporated cities equal
                                a population of 100,000 or more; or (2) the population density of the unincorporated
                                area is equal to, or greater than, the population density of the surrounding cities.
                            </p>
                        )
                    }
                ]
            },
            {
                label: 'Planning',
                fields: [
                    {
                        label: 'Covered by a Specific Plan',
                        field: '2_6',
                        description: (
                            <p>
                                Includes parcels located within the boundaries of city or county specific plans that
                                have been adopted by local governments and submitted to LCI as of October 2023. These
                                specific plans are considered draft until they are verified to have a certified
                                environmental impact report. Note, parcels are captured if the center of the parcel is
                                within this area. Accordingly, some displayed parcels may be partially outside this
                                area.
                            </p>
                        )
                    }
                ]
            },
            {
                label: 'Transit',
                help: 'Parcels that match any of the following options will be included.',
                fields: [
                    {
                        label: '½ mile of an existing Major Transit Stop',
                        field: '3_1',
                        description: (
                            <p>
                                Includes parcels located within ½ mile of an existing major transit stop as defined in
                                PRC 21155 and 21064.3. Note, parcels are captured if the center of the parcel is within
                                this area. Accordingly, some displayed parcels may be partially outside this area. Data
                                for major transit stops was generated by the California Department of Transportation
                                (CalTrans) as of December 2023.
                            </p>
                        )
                    },
                    {
                        label: '½ mile of stops on an existing High-Quality Transit Corridor',
                        field: '3_5',
                        description: (
                            <p>
                                Includes parcels located within a ½ mile of an existing transit stop along an existing
                                high-quality transit corridor, as mentioned in CEQA Guidelines 15064.3. Note, parcels
                                are captured if the center of the parcel is within this area. Accordingly, some
                                displayed parcels may be partially outside this area. Data for existing transit stops
                                and existing high-quality transit corridors were generated by the California Department
                                of Transportation (CalTrans) as of December 2023.
                            </p>
                        )
                    },
                    {
                        label: '¼ mile of an existing High-Quality Transit Corridor',
                        field: '3_2',
                        description: (
                            <p>
                                Includes parcels located within ¼ mile of an existing high-quality transit corridor, as
                                defined in PRC 21155. Note, parcels are captured if the center of the parcel is within
                                this area. Accordingly, some displayed parcels may be partially outside this area. Data
                                for high-quality transit corridors was generated by the California Department of
                                Transportation (CalTrans) as of December 2023.
                            </p>
                        )
                    },
                    {
                        label: '½ mile of an existing High-Quality Transit Corridor',
                        field: '3_4',
                        description: (
                            <p>
                                Includes parcels located within ½ mile of an existing high-quality transit corridor, as
                                defined in PRC 21155. Note, parcels are captured if the center of the parcel is within
                                this area. Accordingly, some displayed parcels may be partially outside this area. Data
                                for high-quality transit corridors was generated by the California Department of
                                Transportation (CalTrans) as of December 2023.
                            </p>
                        )
                    }
                ]
            },
            {
                label: 'Vehicle Miles Traveled',
                help: 'Parcels that match any of the following options will be included.',
                fields: [
                    {
                        label: 'Per Capita VMT: 15% Below Regional Average',
                        field: '3_6',
                        description: (
                            <p>
                                Includes parcels that are located within areas that have 15% below the regional average
                                of household Vehicle Miles Traveled per capita. VMT is calculated by the regional travel
                                demand model using the relevant geography (TAZ or Hex). The data was aggregated from
                                individual MPO's where available (as of 2023), gaps between these MPO Sourced data were
                                filled with the California State Transportation Demand Model (CSTDM) updated as of 2020.
                                Note, parcels are captured if the center of the parcel is within this area. Accordingly,
                                some displayed parcels may be partially outside this area. Use the Layers function to
                                see the boundary lines.
                            </p>
                        )
                    },
                    {
                        label: 'Per Capita VMT: Below Regional Average',
                        field: '3_8',
                        description: (
                            <p>
                                Include parcels that are located within areas that are Below the Regional Average of
                                household Vehicle Miles Traveled (VMT) per capita across the state of California. VMT is
                                calculated by the regional travel demand model using the relevant geography (TAZ or
                                Hex). The data was aggregated from individual MPO's where available (as of 2023), gaps
                                between these MPO Sourced data were filled with the California State Transportation
                                Demand Model (CSTDM) updated in 2020. Note, parcels are captured if the center of the
                                parcel is within this area. Accordingly, some displayed parcels may be partially outside
                                this area. Use the Layers function to see the boundary lines.
                            </p>
                        )
                    }
                ]
            }
        ]
    },
    {
        label: 'Parcels must avoid the following',
        fields: [
            {
                label: 'Environmental Constraints',
                fields: [
                    {
                        label: 'Rare, Threatened and Endangered species',
                        field: '8_5',
                        description: (
                            <p>
                                Excludes parcels that are located in areas that may have rare, threatened, or endangered
                                species. This spatial dataset is derived from the California Natural Diversity Database
                                (CNDDB, 7/30/2023), the United States Fish & Wildlife Service (USFWS) Threatened &
                                Endangered Species Active Critical Habitat (08/15/2023), and the National Land Cover
                                Database 2021 Percent Developed Imperviousness (06/30/2023). This dataset cannot be
                                displayed in the tool as a layer in compliance with CDFW proprietary requirements. While
                                CNDDB is the most current and reliable tool available for tracking occurrences of
                                special-status species statewide, it only contains those records that have previously
                                been reported to CDFW on a voluntary basis. Therefore, the absence of CNDDB records in a
                                given location is not always evidence of absence of rare, threatened, and endangered
                                species.
                            </p>
                        )
                    },
                    {
                        label: 'Special Habitats',
                        field: '8_3',
                        description: (
                            <p>
                                Excludes parcels that are located in habitats which represent the terrestrial vegetation
                                macro groups from the California State Wildlife Action Plan (SWAP) updated by the
                                California Department of Fish and Wildlife in 2015. Note: There is no single data source
                                that provides a comprehensive glimpse of any of the sensitive biological resources in
                                the state. Vegetation Classification and Mapping Program (VegCAMP) data, the BIOS
                                website, and local or regional vegetation maps would need to be reviewed during project
                                specific analyses to help identify potentially occurring sensitive natural communities.
                                This tool does not take the place of parcel-specific or project-level surveys.
                            </p>
                        )
                    },
                    {
                        label: 'Wetlands',
                        field: '8_1',
                        description: (
                            <p>
                                Excludes parcels that intersect wetlands, streams, and riparian areas as depicted in the
                                California Aquatic Resources Inventory dataset (CARI, Version 1.1, April 2023). While
                                CARI is probably the best available data for wetlands in the state, it is not
                                comprehensive. Wetlands could be present even if there are no wetlands identified on a
                                particular site using CARI. The tool does not take the place of parcel-specific or
                                project-level surveys.
                            </p>
                        )
                    },
                    {
                        label: 'Prime farmlands or farmlands of statewide importance',
                        field: '8_6',
                        description: (
                            <p>
                                Excludes parcels from either prime farmland or farmland of statewide importance, as
                                defined pursuant to United States Department of Agriculture land inventory and
                                monitoring criteria, as modified for California, and designated on the maps prepared by
                                the Farmland Mapping and Monitoring Program of the Department of Conservation as of
                                April 2022.
                            </p>
                        )
                    },
                    {
                        label: 'Earthquake/Seismic Fault Zones',
                        field: '9_2',
                        description: (
                            <p>
                                Excludes parcels located within Alquist-Priolo Earthquake Fault Zones as delineated by
                                the California Geological Survey (2022).
                            </p>
                        )
                    },
                    {
                        label: 'State Conservancy Boundary',
                        field: '9_6',
                        description: (
                            <p>
                                Excludes parcels located within the boundaries of the California Natural Resource
                                Agency's State Conservancies.
                            </p>
                        )
                    },
                    {
                        label: 'Riparian Areas',
                        field: '8_2',
                        description: (
                            <p>
                                Excludes parcels located within riparian areas as depicted in the The Natural
                                Communities Commonly Associated with Groundwater (NCCAG) dataset as of February 2023.
                                NCCAG riparian areas are a compilation of phreatophytic vegetation from 45 publicly
                                available state and federal data sets. The extracted vegetation features are considered
                                to be commonly associated with groundwater based on published and/or field observations
                                of phreatophytic characteristics in California. Note: This data set does not provide a
                                comprehensive inventory of riparian habitat in the state. Wetlands could be found on
                                parcels that are not screened-out. The tool does not take the place of parcel-specific
                                or project-level surveys.
                            </p>
                        )
                    },
                    {
                        label: 'Landslide Hazard',
                        field: '9_5',
                        description: (
                            <p>
                                Excludes parcels located in areas of landslide hazard zones, liquefaction zones, or the
                                overlap of landslide and liquefaction zones as delineated by the California Geological
                                Survey (2022).
                            </p>
                        )
                    },
                    {
                        label: 'Flood Plain ',
                        field: '9_4',
                        description: (
                            <p>
                                Excludes parcels in areas designated as zone A, A99, AE, AH, AO, & VE in the National
                                Flood Hazard Layer published by the Federal Emergency and Management Agency
                                (10/15/2019). The flood hazard areas included are the 1 percent chance per year flood
                                zones or 100-year flood hazard areas.
                            </p>
                        )
                    },
                    {
                        label: 'Wildfire Hazard',
                        field: '9_3',
                        description: (
                            <p>
                                Excludes parcels in areas within a very high fire hazard severity zone (VHFHSZ)
                                recommendations in Local Responsibility Areas (LRA), or Fire Hazard Severity Zones
                                (FHSZ) for State Responsibility Areas (SRA) as determined by the Department of Forestry
                                and Fire Protection pursuant to Section 51178. This specific dataset is used to create
                                the official "Maps of Fire Hazard Severity Zones in the State Responsibility Area of
                                California" as required by Public Resources Code 4201-4204 and entitled in the
                                California Code of Regulation, Title 14, Section 1280 Fire Hazard Severity Zones, and as
                                adopted and updated by CAL FIRE on November 7, 2007.
                            </p>
                        )
                    }
                ]
            }
        ]
    }
]

// Groups of screening options which are combined using OR rather than AND
export const combinedScreeningOptions = { transit: ['3_1', '3_2', '3_4', '3_5'], vmt: ['3_6', '3_8'] }

export const basemaps = [
    {
        name: 'Streets',
        style: 'streets-v12',
        url: 'mapbox://styles/mapbox/streets-v12'
    },
    {
        name: 'Light',
        style: 'light-v11',
        url: 'mapbox://styles/mapbox/light-v11'
    },
    {
        name: 'Satellite',
        style: 'satellite-v9',
        url: 'mapbox://styles/mapbox/satellite-v9'
    }
]

export const contextualLayers = [
    {
        id: 'heatmap',
        name: 'Density of Exemptions',
        tileName: 'heatmap',
        tileType: 'heatmap',
        opacity: null,
        style: {
            paint: {
                'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, 8, 1],
                'heatmap-intensity': ['interpolate', ['exponential', 2], ['zoom'], 10, 1, 18, 600],
                'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0,
                    'rgba(255, 255, 255, 0)',
                    0.2,
                    '#dadaeb',
                    0.4,
                    '#bcbddc',
                    0.6,
                    '#9e9ac8',
                    0.8,
                    '#756bb1',
                    1,
                    '#54278f'
                ],
                'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 12, 10, 18, 100],
                'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.8, 15, 0.8, 16, 0.6]
            }
        },
        category: 'Exemption Summary',
        visible: false,
        description: (
            <p>
                The heatmap shows hot spots in areas where parcels are clustered which potentially meet several CEQA
                streamlining options. The higher color intensity is associated with parcels that have relatively more
                exemptions than the ones with lower color intensity. This layer is dynamic and for display purposes
                only.
            </p>
        ),
        legends: [
            {
                type: 'gradient',
                color: [
                    { hex: '#ffffff', position: 0 },
                    { hex: '#dadaeb', position: 20 },
                    { hex: '#bcbddc', position: 40 },
                    { hex: '#9e9ac8', position: 60 },
                    { hex: '#756bb1', position: 80 },
                    { hex: '#54278f', position: 100 }
                ],
                label: [{ name: 'Density of Exemptions', lowLabel: 'Lower', highLabel: 'Higher' }]
            }
        ]
    },
    {
        id: 'parcels',
        name: 'Parcels',
        tileName: 'parcels',
        category: 'Boundaries',
        visible: true,
        opacity: null,
        legends: [
            {
                type: 'rect-solid',
                label: 'Parcels',
                color: 'transparent',
                lineColor: '#8f8f8f'
            }
        ],
        description: (
            <p>The statewide parcel data files are current as of February 2023 and provided by ParcelQuest to OPR.</p>
        )
    },
    {
        id: 'fema_footprints',
        name: 'Building Footprints',
        category: 'Boundaries',
        tileName: 'CA_Structures',
        tileType: 'fill',
        opacity: 0.5,
        style: {
            paint: {
                'fill-color': '#7476D2'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#7476D2'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents statewide structure boundaries (building footprints) as generated from the Federal
                Emergency Management Administration (FEMA) and is current for the most recent year of availability
                (2023). This layer is for visual context only and does not integrate into the Site Check Screen or
                Analyze tools.
            </p>
        ),
        legends: [
            {
                type: 'rect-solid',
                label: 'FEMA Building Footprints',
                color: '#7476D255',
                lineColor: '#7476D2'
            }
        ]
    },
    {
        id: 'census_blocks',
        name: 'Census Blocks',
        category: 'Boundaries',
        tileName: 'tl_2022_06_bg',
        tileType: 'fill',
        opacity: null,
        style: {
            paint: {
                'fill-color': 'transparent'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#F57AB6'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents statewide census block boundaries as generated by the United States Census and is
                current for the most recent year of availability (2022). This layer is for visual context only and does
                not integrate into the Site Check Screen or Analyze tools.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/753b1840d75d4ff7aa854845d76b9e2b/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Census Blocks',
                color: 'transparent',
                lineColor: '#F57AB6'
            }
        ]
    },
    {
        id: 'cityBoundary',
        name: 'City',
        category: 'Boundaries',
        visible: false,
        opacity: null,
        tileName: 'incorp23_1',
        tileType: 'fill',
        style: {
            paint: {
                'fill-color': 'transparent'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#A80000'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents current city boundaries as of May 2023 and generated by the California Department
                of Forestry and Fire Protection (CAL FIRE), which processes and digitally captures annexations sent by
                the state Board of Equalization (BOE).
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/3fcbcd9d9b37493899d61d73f90d6632/',
        legends: [
            {
                type: 'rect-solid',
                label: 'City Boundaries',
                color: 'transparent',
                lineColor: '#A80000'
            }
        ]
    },
    {
        id: 'countyBoundary',
        name: 'County',
        category: 'Boundaries',
        tileName: 'cnty19_1_full',
        tileType: 'fill',
        opacity: null,
        style: {
            paint: {
                'fill-color': 'transparent'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#000000'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents current county boundaries as of 2019 and generated by the California Department of
                Forestry and Fire Protection (CAL FIRE), which processes and digitally captures annexations sent by the
                state Board of Equalization (BOE).
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/ded9b0c58e0c48debc9f1d3cb3bb0acc/',
        legends: [
            {
                type: 'rect-solid',
                label: 'County Boundaries',
                color: 'transparent',
                lineColor: '#000000'
            }
        ]
    },
    {
        id: 'mpo_boundary',
        name: 'Metropolitan Planning Organization',
        category: 'Boundaries',
        tileName: 'MPO_2013',
        tileType: 'fill',
        opacity: null,
        style: {
            hatch: {
                type: 'simple',
                color: '#C500FF'
            },
            outlinePaint: {
                'line-width': 3,
                'line-color': '#C500FF'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                The Metropolitan Planning Organization (MPO) boundaries are as reported by the Census Bureau and current
                as of Jan 1, 2010. An MPO is a designation under federal law that encourages large urbanized areas to
                engage in regional transportation planning.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/41d649c31c594f57a6f6aae0b40db37b',
        legends: [
            {
                type: 'rect-simple-hatch',
                label: 'Metropolitan Planning Organization',
                color: '#C500FF'
            }
        ]
    },
    {
        id: 'urbanizedUrbanAreas',
        name: 'Urbanized Area or urban cluster under Census',
        category: 'Urbanized/Urban Areas',
        tileName: 'req2_7_UrbanizedArea_or_UrbanCluster_202310',
        tileType: 'fill',
        opacity: 0.5,
        style: {
            paint: {
                'fill-color': '#FFFFBE',
                'fill-outline-color': '#6E6E6E'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents Urbanized Areas or Urban Clusters in the urban area boundaries dataset from the
                2020 US Census.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/319d80e57dc7494495570a890cbaafc8/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Urbanized Area or urban cluster under Census',
                color: '#FFFFBE',
                lineColor: '#6E6E6E'
            }
        ]
    },
    {
        id: 'urbanizedArea21071',
        name: 'Urbanized Area under PRC 21071',
        category: 'Urbanized/Urban Areas',
        tileName: 'req2_1_UrbanizedArea_PRC_21071_202310',
        tileType: 'fill',
        opacity: 0.5,
        style: {
            paint: {
                'fill-color': '#FFD37F',
                'fill-outline-color': '#6E6E6E'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents an Urbanized Area as defined in Public Resources Code 21071, which represents
                incorporated cities (CAL FIRE 2021) that meet the criteria in subdivision (a) and unincorporated islands
                (areas completely surrounded by one or more incorporated cities) that meet the criteria in subdivision
                (b)(1)(A).
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/9d93fcf4045245c6981979c24235c792/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Urbanized Area under PRC 21071',
                color: '#FFD37F',
                lineColor: '#6E6E6E'
            }
        ]
    },
    {
        id: 'urbanizedArea21094.5',
        name: 'Urban Area under PRC 21094.5',
        category: 'Urbanized/Urban Areas',
        tileName: 'req2_2_UrbanizedArea_PRC_21094_5_202310',
        tileType: 'fill',
        opacity: 0.5,
        style: {
            paint: {
                'fill-color': '#f7cac9',
                'fill-outline-color': '#6E6E6E'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents an Urban Area as defined in PRC 21094.5, which represents incorporated cities (CAL
                FIRE 2021) and unincorporated islands (areas completely surrounded by one or more incorporated cities)
                that meet the following criteria: (1) the population of the unincorporated area and the population of
                the surrounding incorporated cities equal a population of 100,000 or more; or (2) the population density
                of the unincorporated area is equal to, or greater than, the population density of the surrounding
                cities.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/270c4d215bfd4dc786fb8e90df1fa26b/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Urban Area under PRC 21094.5',
                color: '#f7cac9',
                lineColor: '#6E6E6E'
            }
        ]
    },
    {
        id: 'Zoning_Agricultural',
        name: 'Agricultural',

        category: 'LCI Land Use Categories',
        tileName: 'Zoning_Agricultural_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#F5F57A'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#F5F57A'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <>
                <p>
                    This represents areas zoned for intensive agriculture, extensive agriculture, general agriculture,
                    grazing land and some processing facilities.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Agricultural',
                color: '#F5F57A',
                lineColor: '#F5F57A'
            }
        ]
    },
    {
        id: 'Zoning_Industrial',
        name: 'Industrial',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_Industrial_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#A80000'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#A80000'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for heavy industry and light industry, mixed industrial and commercial,
                    and business parks that allow industrial use.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Industrial',
                color: '#A80000',
                lineColor: '#A80000'
            }
        ]
    },
    {
        id: 'Zoning_High_Int_Com',
        name: 'High Intensity Commercial',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_HighIntCom_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#FFA77F'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#FFA77F'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for downtown commercial, office commercial, highway commercial, civic
                    centers, regional commercial, tourist or visitor commercial, and other land use with the features of
                    high density commercial.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'High Intensity Commercial',
                color: '#FFA77F',
                lineColor: '#FFA77F'
            }
        ]
    },
    {
        id: 'Zoning_Low_Int_Com',
        name: 'Low Intensity Commercial and Public Facilities',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_LowIntCom_PublicFac_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#FFD37F'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#FFD37F'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for neighborhood commercial, general commercial, community commercial,
                    schools, institutional, public/quasi-public facilities, hospitals, and commercial land uses not
                    included in high density commercial.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Low Intensity Commercial and Public Facilities',
                color: '#FFD37F',
                lineColor: '#FFD37F'
            }
        ]
    },
    {
        id: 'Zoning_Mixed_Use_Com',
        name: 'Mixed Use of Residential and Commercial',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_MixedUse_ResCom_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#FFEBBE'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#FFEBBE'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for a "mix of residential or commercial use" or otherwise indicated as
                    "mixed uses allowed" without specification.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Mixed Use of Residential and Commercial',
                color: '#FFEBBE',
                lineColor: '#FFEBBE'
            }
        ]
    },
    {
        id: 'Zoning_Planned_Area',
        name: 'Planned Area',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_UrbReserve_PlanDev_PlanArea_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#F57AB6'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#F57AB6'
            }
        },
        description: (
            <>
                <p>
                    This represents specific plan areas, planned development units, planned districts, urban reserves,
                    or other named planned areas in zoning code, where other or specific allowed uses are not specified.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Planned Area',
                color: '#F57AB6',
                lineColor: '#F57AB6'
            }
        ]
    },
    {
        id: 'Zoning_Urban_Residential',
        name: 'Urban Residential',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_UrbRes_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#704489'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#704489'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for residential use at urban densities of approximately 7 or more
                    dwellings per acre and includes multi-family.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Urban Residential',
                color: '#704489',
                lineColor: '#704489'
            }
        ]
    },
    {
        id: 'Zoning_Suburban_Residential',
        name: 'Suburban Residential',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_SuburbRes_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#AA66CD'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#AA66CD'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for residential use at suburban densities of around .5 to 6 units per
                    acre.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Suburban Residential',
                color: '#AA66CD',
                lineColor: '#AA66CD'
            }
        ]
    },
    {
        id: 'Zoning_ExUrban_Residential',
        name: 'Ex-Urban Residential',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_ExUrbanResidential_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#DF73FF'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#DF73FF'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for residential use at densities of around 2 to 20 acres per dwelling.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Ex-Urban Residential',
                color: '#DF73FF',
                lineColor: '#DF73FF'
            }
        ]
    },
    {
        id: 'Zoning_Rural_Residential',
        name: 'Rural Residential',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_RuralRes_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#E8BEFF'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#E8BEFF'
            }
        },
        description: (
            <>
                <p>
                    This represents areas zoned for residential use with allowed densities of over 20 acres per dwelling
                    unit.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Rural Residential',
                color: '#E8BEFF',
                lineColor: '#E8BEFF'
            }
        ]
    },
    {
        id: 'Zoning_Open_Space',
        name: 'Open Space and Public Lands',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_OpenSpace_PubLands_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#A5F57A'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#A5F57A'
            }
        },
        description: (
            <>
                <p>This includes areas where development is generally not permitted.</p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Open Space and Public Lands',
                color: '#A5F57A',
                lineColor: '#A5F57A'
            }
        ]
    },
    {
        id: 'Zoning_Water',
        name: 'Water',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_Water_20230921',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#6677CD'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#6677CD'
            }
        },
        description: (
            <>
                <p>This represents areas zoned for uses related to water. </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Water',
                color: '#6677CD',
                lineColor: '#6677CD'
            }
        ]
    },
    {
        id: 'Zoning_Other',
        name: 'Other - Not Determined',
        category: 'LCI Land Use Categories',
        tileName: 'Zoning_OtherNotDetermined_20231129',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#E1E1E1'
            },
            outlinePaint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 12, 3],
                'line-color': '#E1E1E1'
            }
        },
        description: (
            <>
                <p>
                    This includes tribal and military uses, transportation uses, unassigned land, unknown designations,
                    and otherwise unclassified uses.
                </p>
                <p>
                    Background: LCI staff interpreted zoning code to categorize parcels statewide into thirteen general
                    land use categories for the purposes of displaying land use information in Site Check. All
                    information displayed comes from public sources. Please see the "Methods" section for more
                    information about the information displayed.
                </p>
                <p>
                    Disclaimer: LCI makes no representations about the accuracy of this classification, and it is
                    provided here for informational purposes only. All users should independently confirm land use
                    designations associated with parcels.
                </p>
            </>
        ),
        externalLink: 'https://gis.data.ca.gov/datasets/Gov-OPR::california-statewide-zoning/about',
        legends: [
            {
                type: 'rect-solid',
                label: 'Other - Not Determined',
                color: '#E1E1E1',
                lineColor: '#E1E1E1'
            }
        ]
    },
    {
        id: 'specificPlan',
        name: 'Covered by a Specific Plan',
        category: 'Planning',
        tileName: 'req2_6_SpecificPlan_Coverage_20240226',
        tileType: 'fill',
        opacity: null,
        style: {
            hatch: {
                type: 'cross',
                color: '#FFFF00'
            },
            outlinePaint: {
                'line-width': 3,
                'line-color': '#FFFF00'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents the boundaries of City and County specific plans that have been adopted by local
                governments and submitted to LCI as of October, 2023. There are likely specific plans that have not been
                included in this tool. The included specific plans may or may not have a certified environmental impact
                report.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/0c91ba6558ad4cbf9cd2ce1ee5e4752d/',
        legends: [
            {
                type: 'rect-crosshatch',
                label: 'Covered by a Specific Plan',
                color: '#f0f00a'
            }
        ]
    },
    {
        id: 'existingMajorTransitStops',
        name: 'Existing Major Transit Stops',
        category: 'Transit',
        tileName: 'Existing_Major_Transit_Stops_202312',
        tileType: 'circle',
        opacity: null,
        style: {
            paint: {
                'circle-color': '#FFFF00',
                'circle-stroke-color': '#000000',
                'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 12, 1]
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents existing major transit stops, as defined in Public Resources Code (PRC) 21155 and
                21064.3, and generated by the California Department of Transportation (CalTrans) as of December 2023.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/641fd566ce784f1db1e7df6aef143043/',
        legends: [
            {
                type: 'point',
                label: 'Existing Major Transit Stops',
                color: '#FFFF00',
                lineColor: '#000000'
            }
        ]
    },
    {
        id: 'existingHQTC',
        name: 'Existing High-Quality Transit Corridors',
        category: 'Transit',
        tileName: 'HQ_BusCorridors_Statewide_202312',
        tileType: 'line',
        opacity: null,
        style: {
            paint: {
                'line-color': '#4B0082',
                'line-width': 2
            }
        },
        url: `${tileserverRoot}3_2_3_4_existing_high_quality_transit_corridors/tiles/{z}/{x}/{y}.png`,
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents existing high-quality transit corridors, as defined in Public Resources Code (PRC)
                21155, and derived from data generated by the California Department of Transportation (CalTrans) as of
                December 2023.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/c6709236a7184f3d8e9b7c1e0d95087c/',
        legends: [
            {
                type: 'line',
                label: 'Existing High-Quality Transit Corridors',
                color: '#4B0082'
            }
        ]
    },
    {
        id: 'existingHQTC21155',
        name: 'Stops along existing High-Quality Transit Corridors as defined in PRC 21155',
        category: 'Transit',
        tileName: 'Stops_along_HQ_Corridor_202312',
        tileType: 'circle',
        opacity: null,
        style: {
            paint: {
                'circle-color': '#ff5501',
                'circle-stroke-color': '#000000',
                'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 12, 1]
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents existing transit stops along existing high-quality transit corridors, as mentioned
                in CEQA Guidelines 15064.3, and derived from data generated by the California Department of
                Transportation (CalTrans) as of December 2023.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/b5c965dfc60e4d65b1892db385bacf67/',
        legends: [
            {
                type: 'point',
                label: 'Stops along existing High-Quality Transit Corridors as defined in PRC 21155',
                color: '#ff5500',
                lineColor: '#000000'
            }
        ]
    },
    {
        id: 'vmt15PercentBelowRegionalAverage',
        name: 'Per Capita VMT: 15% Below Regional Average',
        category: 'Vehicle Miles Traveled',
        tileName: 'req3_6_Per_Capita_VMT_15pt_BelAvg',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#FF00C5',
                'fill-outline-color': '#FF00C5'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                These data represent 15% Below Regional Average Vehicle Miles Traveled (VMT), per capita across the
                state of California. VMT is calculated by the regional travel demand model using the relevant geography
                (TAZ or Hex). The data was aggregated from individual MPO's where available (as of 2023), gaps between
                these MPO Sourced data were filled with the California State Transportation Demand Model (CSTDM) updated
                as of 2023. Here, "15% Below Regional Average VMT" are Percent VMT values that are less than 85%,
                calculated as VMT Per Capita divided by the Regional Average VMT Per Capita. Regional Average VMT Per
                Capita values were calculated as the sum of the resident household based VMT divided by the sum of the
                population across an MPO.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/4e189c878e094cbfb0a000ae8ad04948/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Per Capita VMT: 15% Below Regional Average',
                color: '#FF00C5'
            }
        ]
    },
    {
        id: 'vmtBelowRegionalAverage',
        name: 'Per Capita VMT: Below Regional Average',
        category: 'Vehicle Miles Traveled',
        tileName: 'req3_8_Per_Capita_VMT_BelAvg',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#A80084',
                'fill-outline-color': '#A80084'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                These data represent Below Regional Average Vehicle Miles Traveled (VMT), per capita across the state of
                California. VMT is calculated by the regional travel demand model using the relevant geography (TAZ or
                Hex). The data was aggregated from individual MPO's where available (as of 2023), gaps between these MPO
                Sourced data were filled with the California State Transportation Demand Model (CSTDM) updated in 2020.
                Here, "Below Regional Average VMT" are Percent VMT values that are between 99-86%, calculated as VMT Per
                Capita divided by the Regional Average VMT Per Capita. Regional Average VMT Per Capita values were
                calculated as the sum of the resident household based VMT divided by the sum of the population across an
                MPO.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/d155d4bec8774c7db030ab090bd86359/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Per Capita VMT: Below Regional Average',
                color: '#A80084'
            }
        ]
    },
    {
        id: 'specialHabitats',
        name: 'Special Habitats',
        category: 'Environmental Constraints',
        tileName: 'Special_Habitats',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#38A800',
                'fill-outline-color': 'transparent'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents habitats which represent the terrestrial vegetation macro groups from the
                California State Wildlife Action Plan (SWAP) updated by the California Department of Fish and Wildlife
                in 2015. Note: There is no single data source that provides a comprehensive glimpse of any of the
                sensitive biological resources in the state. Vegetation Classification and Mapping Program (VegCAMP)
                data, the BIOS website, and local or regional vegetation maps would need to be reviewed during project
                specific analyses to help identify potentially occurring sensitive natural communities. This tool does
                not take the place of parcel-specific or project-level surveys.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/1cfc5d39fdc84647b7ce55ff92426bd3/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Special Habitats',
                color: '#38A800'
            }
        ]
    },
    {
        id: 'primeFarmlands',
        name: 'Prime farmlands or farmlands of statewide importance',
        category: 'Environmental Constraints',
        tileName: 'req8_6_Farmland_Prime_StatewideImportance',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#F5F57A',
                'fill-outline-color': 'transparent'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents either prime farmland or farmland of statewide importance, as defined pursuant to
                United States Department of Agriculture land inventory and monitoring criteria, as modified for
                California, and designated on the maps prepared by the Farmland Mapping and Monitoring Program of the
                Department of Conservation as of April 2022.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/24b1bd517d1e461b9dea7fc72e3dda3d/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Prime farmlands or farmlands of statewide importance',
                color: '#F5F57A'
            }
        ]
    },
    {
        id: 'wetlands',
        name: 'Wetlands',
        category: 'Environmental Constraints',
        tileName: 'CARI_Wetlands',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#00FFC5'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents wetlands, streams, and riparian areas all standardized to a common wetland
                classification system as part of the California Aquatic Resources Inventory (CARI, Version 1.1), and is
                current as of April 2023. While CARI is probably the best available data for wetlands in the state, it
                is not comprehensive. Wetlands could be present even if there are no wetlands identified on a particular
                site using CARI. The tool does not take the place of parcel-specific or project-level surveys.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/bf488e8504a24510b7ce88d1567b4ba6/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Wetlands',
                color: '#00FFC5'
            }
        ]
    },
    {
        id: 'earthquakeZones',
        name: 'Earthquake/Seismic Fault Zones',
        category: 'Environmental Constraints',
        tileName: 'CGS_Alquist_Priolo_Fault_Zones',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            hatch: {
                type: 'cross',
                color: '#730000'
            },
            outlinePaint: {
                'line-width': 3,
                'line-color': '#730000'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents the extent of Alquist-Priolo Earthquake Fault Zones as delineated by the
                California Geological Survey (2022).
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/d4e8e99004f2430ca80d05ee261b2103/',
        legends: [
            {
                type: 'rect-crosshatch',
                label: 'Earthquake/Seismic Fault Zones',
                color: '#730000'
            }
        ]
    },
    {
        id: 'stateConservancy',
        name: 'State Conservancy Boundary',
        category: 'Environmental Constraints',
        tileName: 'ds1754',
        tileType: 'fill',
        opacity: 0.9,
        style: {
            hatch: {
                type: 'cross',
                color: '#00734C'
            },
            outlinePaint: {
                'line-width': 3,
                'line-color': '#00734C'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>This layer represents the boundaries of the California Natural Resource Agency's State Conservancies.</p>
        ),
        dbLink: 'https://databasin.org/datasets/9d0ab79e126647ac821a28dd0c456f77',
        legends: [
            {
                type: 'rect-crosshatch',
                label: 'State Conservancy Boundary',
                color: '#00734C'
            }
        ]
    },
    {
        id: 'riparianAreas',
        name: 'Riparian Areas',
        category: 'Environmental Constraints',
        tileName: 'i02_NCCAG_Vegetation',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#00A9E6',
                'fill-outline-color': 'transparent'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents vegetation features commonly associated with groundwater based on published and/or
                field observations of phreatophytic characteristics. It is a compilation of phreatophytic vegetation
                from 45 publicly available state and federal data set, and curated within the The Natural Communities
                Commonly Associated with Groundwater (NCCAG) dataset, which is current as of February 2023. This dataset
                does not provide a comprehensive inventory of riparian habitat in the state. Wetlands could be found on
                parcels that are not screened-out. The tool does not take the place of parcel-specific or project-level
                surveys.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/34e7670e42e74206807d67eb5062ac57/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Riparian Areas',
                color: '#00A9E6'
            }
        ]
    },
    {
        id: 'landslideHazard',
        name: 'Landslide Hazard',
        category: 'Environmental Constraints',
        tileName: 'req9_5_LandslideHazard_20240118',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#a87000',
                'fill-outline-color': 'transparent'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This dataset represents a composite of landslide hazard zones, liquefaction zones, and the overlap of
                landslide and liquefaction zones as delineated by the California Geological Survey (2022).
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/8caa4e91ea5642c6bdc72308e6e4dc4f/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Landslide Hazard',
                color: '#a87000'
            }
        ]
    },
    {
        id: 'floodplain',
        name: 'Flood Plain',
        category: 'Environmental Constraints',
        tileName: 'req9_4_FloodPlain',
        tileType: 'fill',
        opacity: 0.9,
        style: {
            hatch: {
                type: 'simple',
                color: '#004DA8'
            },
            outlinePaint: {
                'line-width': 1.5,
                'line-color': '#004DA8'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This layer represents areas designated as zone A, A99, AE, AH, AO, & VE in the National Flood Hazard
                Layer published by the Federal Emergency and Management Agency (10/15/2019). The flood hazard areas
                included are the 1 percent chance per year flood zones or 100-year flood hazard areas.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/845bd265f7604fd499da8620b5d6009f',
        legends: [
            {
                type: 'rect-simple-hatch',
                label: 'Flood Plain',
                color: '#004DA8'
            }
        ]
    },
    {
        id: 'wildfireHazard',
        name: 'Wildfire Hazard',
        category: 'Environmental Constraints',
        tileName: 'fhszs06_3',
        tileType: 'fill',
        opacity: 0.75,
        style: {
            paint: {
                'fill-color': '#FA5F55',
                'fill-outline-color': 'transparent'
            }
        },
        subdomains: [],
        attribution: '',
        description: (
            <p>
                This dataset includes very high fire hazard severity zone (VHFHSZ) recommendations in Local
                Responsibility Areas (LRA), and Fire Hazard Severity Zones (FHSZ) for State Responsibility Areas (SRA)
                as determined by the Department of Forestry and Fire Protection pursuant to Section 51178 and as adopted
                and updated by CAL FIRE on November 7, 2007.
            </p>
        ),
        dbLink: 'https://databasin.org/datasets/fbb8a20def844e168aeb7beb1a7e74bc/',
        legends: [
            {
                type: 'rect-solid',
                label: 'Wildfire Hazard',
                color: '#FA5F55'
            }
        ]
    }
]
